// ════════════════════════════════════════════════════════════════════════════
//  ContaCorrenteDialog — formulário completo com 4 tabs:
//    1. Geral             (dados básicos + saldo/limite)
//    2. Cobrança          (CNAB, multa, juros, layouts, diretórios)
//    3. PIX               (chave + credenciais ClienteID/Secret)
//    4. Integração API    (Inter/ACBr — ClientID/Secret + .crt/.key)
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Upload, Loader2, FileKey, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useBancosCombo } from '../hooks/useCadastrosFinanceiros';
import { cadastrosFinanceirosService } from '../services/cadastrosFinanceirosService';
import { getApiErrorMessage } from '@/lib/apiClient';
import type {
  ContaCorrente, SalvarContaCorrenteRequest, ContaCorrenteIntegracao,
} from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SalvarContaCorrenteRequest) => void;
  editingConta?: ContaCorrente | null;
  isLoading?: boolean;
}

const defaultIntegracao: ContaCorrenteIntegracao = {
  contaCorrenteId: 0,
  imprimeMensagemPadrao: true,
  mostraPreview: false,
  removeAcento: true,
  lerCedente: false,
  qtdCopias: 1,
  ambiente: 0,
  tipoIntegracao: 0,
};

const defaultValues: SalvarContaCorrenteRequest = {
  descricao: '',
  isAtivo: true,
  bancoId: undefined,
  numeroConta: '',
  codigoAgencia: '',
  carteira: '',
  cedente: '',
  convenio: '',
  especieDocumento: '',
  modalidade: '',
  jurosMora: 0,
  taxaEmissaoBoleto: 0,
  multaPercentual: 0,
  multaValor: 0,
  descontoPercentual: 0,
  descontoValor: 0,
  limite: 0,
  saldoAtual: 0,
  nossoNumeroInicio: 0,
  nossoNumeroFim: 0,
  nossoNumeroAtual: 0,
  diasProtesto: 0,
  descricaoBoleto: '',
  localPagamento: '',
  chavePix: '',
  diretorioRemessa: '',
  diretorioRetorno: '',
  nomeRemessa: '',
  nomeRetorno: '',
  layoutLote: '',
  layoutArquivo: '',
  ofxPadraoInternacional: false,
  numeroSequencial: 0,
  clienteIdPix: '',
  clienteSecretPix: '',
};

export const ContaCorrenteDialog = ({ open, onOpenChange, onSubmit, editingConta, isLoading }: Props) => {
  const { toast } = useToast();
  const form = useForm<SalvarContaCorrenteRequest>({ defaultValues });
  const { bancos } = useBancosCombo();

  // Estado para tab "Integração API"
  const [integracao, setIntegracao] = useState<ContaCorrenteIntegracao>(defaultIntegracao);
  const [salvandoIntegracao, setSalvandoIntegracao] = useState(false);
  const [uploadingCrt, setUploadingCrt] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(false);

  // Carrega dados ao abrir o dialog
  useEffect(() => {
    if (editingConta) {
      form.reset({
        descricao: editingConta.descricao,
        bancoId: editingConta.bancoId,
        numeroConta: editingConta.numeroConta || '',
        codigoAgencia: editingConta.codigoAgencia || '',
        carteira: editingConta.carteira || '',
        cedente: editingConta.cedente || '',
        convenio: editingConta.convenio || '',
        especieDocumento: editingConta.especieDocumento || '',
        modalidade: editingConta.modalidade || '',
        jurosMora: editingConta.jurosMora ?? 0,
        taxaEmissaoBoleto: editingConta.taxaEmissaoBoleto ?? 0,
        multaPercentual: editingConta.multaPercentual ?? 0,
        multaValor: editingConta.multaValor ?? 0,
        descontoPercentual: editingConta.descontoPercentual ?? 0,
        descontoValor: editingConta.descontoValor ?? 0,
        limite: editingConta.limite ?? 0,
        saldoAtual: editingConta.saldoAtual ?? 0,
        nossoNumeroInicio: editingConta.nossoNumeroInicio ?? 0,
        nossoNumeroFim: editingConta.nossoNumeroFim ?? 0,
        nossoNumeroAtual: editingConta.nossoNumeroAtual ?? 0,
        diasProtesto: editingConta.diaProtesto ?? 0,
        descricaoBoleto: editingConta.descricaoBoleto || '',
        localPagamento: editingConta.localPagamento || '',
        chavePix: editingConta.chavePix || '',
        diretorioRemessa: editingConta.diretorioRemessa || '',
        diretorioRetorno: editingConta.diretorioRetorno || '',
        nomeRemessa: editingConta.nomeRemessa || '',
        nomeRetorno: editingConta.nomeRetorno || '',
        layoutLote: editingConta.layoutLote || '',
        layoutArquivo: editingConta.layoutArquivo || '',
        bancoAcbrId: editingConta.bancoAcbrId,
        digitoContaId: editingConta.digitoContaId,
        digitoAgenciaId: editingConta.digitoAgenciaId,
        tipoIntegracao: editingConta.tipoIntegracao,
        tipoCarteira: editingConta.tipoCarteira,
        tipoDocumento: editingConta.tipoDocumento,
        caracteristicaCobranca: editingConta.caracteristicaCobranca,
        ofxPadraoInternacional: editingConta.ofxPadraoInternacional ?? false,
        numeroSequencial: editingConta.numeroSequencial ?? 0,
        clienteIdPix: editingConta.clienteIdPix || '',
        clienteSecretPix: editingConta.clienteSecretPix || '',
        isAtivo: editingConta.isAtivo,
      });

      // Carrega config de integração (se já existe)
      cadastrosFinanceirosService.getIntegracaoContaCorrente(editingConta.id)
        .then(setIntegracao)
        .catch(() => setIntegracao({ ...defaultIntegracao, contaCorrenteId: editingConta.id }));
    } else {
      form.reset(defaultValues);
      setIntegracao(defaultIntegracao);
    }
  }, [editingConta, open, form]);

  // Salvar config integração (ação separada)
  const handleSalvarIntegracao = useCallback(async () => {
    if (!editingConta) {
      toast({ title: 'Salve a conta corrente primeiro.', variant: 'destructive' });
      return;
    }
    setSalvandoIntegracao(true);
    try {
      await cadastrosFinanceirosService.salvarIntegracaoContaCorrente(editingConta.id, integracao);
      toast({ title: 'Configuração de integração salva.' });
    } catch (e) {
      toast({ title: 'Erro ao salvar integração', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setSalvandoIntegracao(false);
    }
  }, [editingConta, integracao, toast]);

  // Upload de certificados
  const handleUploadCert = useCallback(async (file: File, tipo: 'CRT' | 'KEY') => {
    if (!editingConta) {
      toast({ title: 'Salve a conta corrente primeiro.', variant: 'destructive' });
      return;
    }
    if (tipo === 'CRT') setUploadingCrt(true); else setUploadingKey(true);
    try {
      const r = await cadastrosFinanceirosService.uploadCertificadoContaCorrente(
        editingConta.id, file, tipo);
      setIntegracao((prev) => ({
        ...prev,
        ...(tipo === 'CRT' ? { localArquivoCrt: r.caminho } : { localArquivoKey: r.caminho }),
      }));
      toast({ title: `${tipo === 'CRT' ? 'Certificado' : 'Chave privada'} enviado(a).` });
    } catch (e) {
      toast({ title: 'Erro no upload', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      if (tipo === 'CRT') setUploadingCrt(false); else setUploadingKey(false);
    }
  }, [editingConta, toast]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingConta ? 'Editar Conta Corrente' : 'Nova Conta Corrente'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Tabs defaultValue="geral">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="geral">1. Geral</TabsTrigger>
                <TabsTrigger value="cobranca">2. Cobrança</TabsTrigger>
                <TabsTrigger value="pix">3. PIX</TabsTrigger>
                <TabsTrigger value="integracao" disabled={!editingConta}>4. Integração API</TabsTrigger>
              </TabsList>

              {/* ── TAB 1: GERAL ─────────────────────────────────────────── */}
              <TabsContent value="geral" className="space-y-4 mt-4">
                <FormField control={form.control} name="descricao" rules={{ required: 'Descrição é obrigatória' }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição *</FormLabel>
                      <FormControl><Input placeholder="Ex: Conta Principal Inter" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField control={form.control} name="bancoId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Banco</FormLabel>
                      <Select onValueChange={v => field.onChange(v ? Number(v) : undefined)} value={field.value?.toString()}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione o banco" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {bancos.map(b => (
                            <SelectItem key={b.id} value={b.id.toString()}>
                              {b.codigoBanco} - {b.descricao}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-4 gap-3">
                  <FormField control={form.control} name="codigoAgencia" render={({ field }) => (
                    <FormItem><FormLabel>Agência</FormLabel><FormControl><Input placeholder="0000" {...field} /></FormControl></FormItem>)} />
                  <FormField control={form.control} name="digitoAgenciaId" render={({ field }) => (
                    <FormItem><FormLabel>Dig. Ag.</FormLabel><FormControl><Input type="number" {...field} value={field.value ?? ''} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} /></FormControl></FormItem>)} />
                  <FormField control={form.control} name="numeroConta" render={({ field }) => (
                    <FormItem><FormLabel>Conta</FormLabel><FormControl><Input placeholder="00000-0" {...field} /></FormControl></FormItem>)} />
                  <FormField control={form.control} name="digitoContaId" render={({ field }) => (
                    <FormItem><FormLabel>Dig. CC</FormLabel><FormControl><Input type="number" {...field} value={field.value ?? ''} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} /></FormControl></FormItem>)} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="limite" render={({ field }) => (
                    <FormItem><FormLabel>Limite</FormLabel><FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                  <FormField control={form.control} name="saldoAtual" render={({ field }) => (
                    <FormItem><FormLabel>Saldo Atual</FormLabel><FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="ofxPadraoInternacional" render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <FormLabel className="text-sm">OFX Internacional</FormLabel>
                      <FormControl><Switch checked={!!field.value} onCheckedChange={field.onChange} /></FormControl>
                    </FormItem>)} />
                  <FormField control={form.control} name="isAtivo" render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <FormLabel className="text-sm">Ativa</FormLabel>
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                    </FormItem>)} />
                </div>
              </TabsContent>

              {/* ── TAB 2: COBRANÇA ──────────────────────────────────────── */}
              <TabsContent value="cobranca" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="carteira" render={({ field }) => (
                    <FormItem><FormLabel>Carteira</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>)} />
                  <FormField control={form.control} name="cedente" render={({ field }) => (
                    <FormItem><FormLabel>Cedente</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>)} />
                  <FormField control={form.control} name="convenio" render={({ field }) => (
                    <FormItem><FormLabel>Convênio</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>)} />
                  <FormField control={form.control} name="especieDocumento" render={({ field }) => (
                    <FormItem><FormLabel>Espécie Doc.</FormLabel><FormControl><Input {...field} placeholder="DM, DS, NP..." /></FormControl></FormItem>)} />
                  <FormField control={form.control} name="modalidade" render={({ field }) => (
                    <FormItem><FormLabel>Modalidade</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>)} />
                  <FormField control={form.control} name="taxaEmissaoBoleto" render={({ field }) => (
                    <FormItem><FormLabel>Taxa Emissão (R$)</FormLabel><FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <FormField control={form.control} name="jurosMora" render={({ field }) => (
                    <FormItem><FormLabel>Juros Mora (%)</FormLabel><FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                  <FormField control={form.control} name="multaPercentual" render={({ field }) => (
                    <FormItem><FormLabel>Multa (%)</FormLabel><FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                  <FormField control={form.control} name="multaValor" render={({ field }) => (
                    <FormItem><FormLabel>Multa (R$)</FormLabel><FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                  <FormField control={form.control} name="descontoPercentual" render={({ field }) => (
                    <FormItem><FormLabel>Desconto (%)</FormLabel><FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                  <FormField control={form.control} name="descontoValor" render={({ field }) => (
                    <FormItem><FormLabel>Desconto (R$)</FormLabel><FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                  <FormField control={form.control} name="diasProtesto" render={({ field }) => (
                    <FormItem><FormLabel>Dias Protesto</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <FormField control={form.control} name="nossoNumeroInicio" render={({ field }) => (
                    <FormItem><FormLabel>Nosso Nº Início</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                  <FormField control={form.control} name="nossoNumeroFim" render={({ field }) => (
                    <FormItem><FormLabel>Nosso Nº Fim</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                  <FormField control={form.control} name="nossoNumeroAtual" render={({ field }) => (
                    <FormItem><FormLabel>Nosso Nº Atual</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl></FormItem>)} />
                </div>

                <FormField control={form.control} name="descricaoBoleto" render={({ field }) => (
                  <FormItem><FormLabel>Descrição do Boleto</FormLabel><FormControl><Input {...field} /></FormControl></FormItem>)} />
                <FormField control={form.control} name="localPagamento" render={({ field }) => (
                  <FormItem><FormLabel>Local de Pagamento</FormLabel><FormControl><Input {...field} placeholder="Pagável em qualquer banco" /></FormControl></FormItem>)} />

                <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
                  <h4 className="text-sm font-semibold">CNAB — Layouts e diretórios</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="layoutArquivo" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Layout Arquivo</FormLabel><FormControl><Input {...field} placeholder="240" /></FormControl></FormItem>)} />
                    <FormField control={form.control} name="layoutLote" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Layout Lote</FormLabel><FormControl><Input {...field} placeholder="030" /></FormControl></FormItem>)} />
                    <FormField control={form.control} name="diretorioRemessa" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Diretório Remessa</FormLabel><FormControl><Input {...field} placeholder="C:\Cnab\Remessa" /></FormControl></FormItem>)} />
                    <FormField control={form.control} name="diretorioRetorno" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Diretório Retorno</FormLabel><FormControl><Input {...field} placeholder="C:\Cnab\Retorno" /></FormControl></FormItem>)} />
                    <FormField control={form.control} name="nomeRemessa" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Padrão Nome Remessa</FormLabel><FormControl><Input {...field} placeholder="REM" /></FormControl></FormItem>)} />
                    <FormField control={form.control} name="nomeRetorno" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Padrão Nome Retorno</FormLabel><FormControl><Input {...field} placeholder="RET" /></FormControl></FormItem>)} />
                  </div>
                </div>

                <FormField control={form.control} name="aceite" render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <FormLabel className="text-sm">Aceite no boleto</FormLabel>
                    <FormControl><Switch checked={!!field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>)} />
              </TabsContent>

              {/* ── TAB 3: PIX ───────────────────────────────────────────── */}
              <TabsContent value="pix" className="space-y-4 mt-4">
                <FormField control={form.control} name="chavePix" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Chave PIX</FormLabel>
                    <FormControl><Input placeholder="CPF, CNPJ, e-mail, telefone ou aleatória" {...field} /></FormControl>
                  </FormItem>)} />

                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="clienteIdPix" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client ID PIX</FormLabel>
                      <FormControl><Input {...field} placeholder="UUID fornecido pelo banco" /></FormControl>
                    </FormItem>)} />
                  <FormField control={form.control} name="clienteSecretPix" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Secret PIX</FormLabel>
                      <FormControl><Input type="password" {...field} placeholder="••••••••" /></FormControl>
                    </FormItem>)} />
                </div>

                <div className="text-sm text-muted-foreground p-3 border rounded-lg bg-muted/30">
                  <p className="font-semibold mb-1">💡 Configuração avançada</p>
                  <p>Certificados (.crt/.key/.pfx), proxy e ambiente são configurados na aba <strong>Integração API</strong>.</p>
                </div>
              </TabsContent>

              {/* ── TAB 4: INTEGRAÇÃO API (Inter / ACBr) ──────────────────── */}
              <TabsContent value="integracao" className="space-y-4 mt-4">
                {!editingConta ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Salve a conta corrente primeiro para configurar a integração.
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Tipo de Integração</label>
                        <Select
                          value={(integracao.tipoIntegracao ?? 0).toString()}
                          onValueChange={(v) => setIntegracao({ ...integracao, tipoIntegracao: Number(v) })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">ACBr (arquivo CNAB)</SelectItem>
                            <SelectItem value="1">Banco Inter (API)</SelectItem>
                            <SelectItem value="2">Outro API</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Ambiente</label>
                        <Select
                          value={(integracao.ambiente ?? 0).toString()}
                          onValueChange={(v) => setIntegracao({ ...integracao, ambiente: Number(v) })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Homologação</SelectItem>
                            <SelectItem value="1">Produção</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Client ID</label>
                        <Input
                          value={integracao.clientId ?? ''}
                          onChange={(e) => setIntegracao({ ...integracao, clientId: e.target.value })}
                          placeholder="UUID do app no portal Inter"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Client Secret</label>
                        <Input
                          type="password"
                          value={integracao.clientSecret ?? ''}
                          onChange={(e) => setIntegracao({ ...integracao, clientSecret: e.target.value })}
                          placeholder="••••••••••••••••"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Scope</label>
                        <Input
                          value={integracao.scope ?? ''}
                          onChange={(e) => setIntegracao({ ...integracao, scope: e.target.value })}
                          placeholder="boleto-cobranca.read pagamento-pix.write"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Key User (opcional)</label>
                        <Input
                          value={integracao.keyUser ?? ''}
                          onChange={(e) => setIntegracao({ ...integracao, keyUser: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Upload de certificados mTLS */}
                    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
                      <h4 className="text-sm font-semibold flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />Certificado mTLS (Banco Inter)
                      </h4>

                      <div className="space-y-2">
                        <label className="text-xs font-medium flex items-center gap-2">
                          <FileKey className="h-3 w-3" />Certificado .crt
                          {integracao.localArquivoCrt && (
                            <Badge variant="outline" className="text-xs ml-auto">Configurado</Badge>
                          )}
                        </label>
                        <div className="flex gap-2">
                          <Input
                            value={integracao.localArquivoCrt ?? ''}
                            readOnly
                            placeholder="Nenhum arquivo enviado"
                            className="font-mono text-xs h-9"
                          />
                          <label htmlFor="upload-crt">
                            <Button type="button" variant="outline" size="sm" disabled={uploadingCrt} asChild>
                              <span className="cursor-pointer">
                                {uploadingCrt ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                              </span>
                            </Button>
                            <input id="upload-crt" type="file" accept=".crt,.pem" className="hidden"
                              onChange={(e) => e.target.files?.[0] && handleUploadCert(e.target.files[0], 'CRT')} />
                          </label>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium flex items-center gap-2">
                          <FileKey className="h-3 w-3" />Chave privada .key
                          {integracao.localArquivoKey && (
                            <Badge variant="outline" className="text-xs ml-auto">Configurada</Badge>
                          )}
                        </label>
                        <div className="flex gap-2">
                          <Input
                            value={integracao.localArquivoKey ?? ''}
                            readOnly
                            placeholder="Nenhum arquivo enviado"
                            className="font-mono text-xs h-9"
                          />
                          <label htmlFor="upload-key">
                            <Button type="button" variant="outline" size="sm" disabled={uploadingKey} asChild>
                              <span className="cursor-pointer">
                                {uploadingKey ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                              </span>
                            </Button>
                            <input id="upload-key" type="file" accept=".key,.pem" className="hidden"
                              onChange={(e) => e.target.files?.[0] && handleUploadCert(e.target.files[0], 'KEY')} />
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <label className="text-sm">Usa Certificado HTTP</label>
                        <Switch
                          checked={(integracao.useCertificateHttp ?? 0) === 1}
                          onCheckedChange={(v) => setIntegracao({ ...integracao, useCertificateHttp: v ? 1 : 0 })}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <label className="text-sm">Log de Registro</label>
                        <Switch
                          checked={(integracao.logRegistro ?? 0) === 1}
                          onCheckedChange={(v) => setIntegracao({ ...integracao, logRegistro: v ? 1 : 0 })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <label className="text-xs">Imprime Msg</label>
                        <Switch
                          checked={integracao.imprimeMensagemPadrao}
                          onCheckedChange={(v) => setIntegracao({ ...integracao, imprimeMensagemPadrao: v })}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <label className="text-xs">Preview</label>
                        <Switch
                          checked={integracao.mostraPreview}
                          onCheckedChange={(v) => setIntegracao({ ...integracao, mostraPreview: v })}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <label className="text-xs">Remove Acentos</label>
                        <Switch
                          checked={integracao.removeAcento}
                          onCheckedChange={(v) => setIntegracao({ ...integracao, removeAcento: v })}
                        />
                      </div>
                    </div>

                    <Button type="button" variant="secondary" onClick={handleSalvarIntegracao} disabled={salvandoIntegracao} className="w-full">
                      {salvandoIntegracao
                        ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Salvando configuração...</>
                        : 'Salvar Configuração de Integração'}
                    </Button>
                  </>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Salvando...' : editingConta ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
